import { api } from './client';

/** 与 Excel「投诉零件清单」及 GET /imports/current 行对象一致 */
export interface ComplaintRow {
  sourceRow: number;
  date: string;
  deptC: string | null;
  deptP: string;
  jz: string | null;
  pm: string | null;
  lh: string | null;
  yc: string | null;
  cause: string | null;
  temp: string | null;
  permanent: string | null;
  fzr: string | null;
  replyPlan: string | null;
  replyActual: string | null;
  replyOntime: string | null;
  defectPlan: string | null;
  defectActual: string | null;
  defectOntime: string | null;
  closePlan: string | null;
  closeActual: string | null;
  closeOntime: string | null;
  repeat: string | null;
  week: number | null;
  month: number | null;
}

export interface DepartmentCount {
  name: string;
  count: number;
}

export interface Rate {
  filled: number;
  total: number;
  percent: number | null;
}

export interface DepartmentStat {
  name: string;
  count: number;
  defect: Rate;
  reply: Rate;
  close: Rate;
}

export interface ComplaintSummary {
  total: number;
  defect: Rate;
  reply: Rate;
  close: Rate;
  departments: DepartmentStat[];
}

export interface CurrentImport {
  batchId: string;
  /** 导入完成时刻（毫秒时间戳） */
  finishedAt: number;
  /** 源文件修改时刻（毫秒时间戳） */
  fileModifiedAt: number | null;
  filePath: string;
  fileName: string;
  groupKey: string;
  templateLabel: string;
  total: number;
  defectActualCount: number;
  replyActualCount: number;
  closeActualCount: number;
  departments: DepartmentCount[];
  summary: ComplaintSummary;
  rows: ComplaintRow[];
}

export function fetchCurrent(): Promise<{ current: CurrentImport | null; source: ImportSourceConfig }> {
  return api.get('/imports/current').then((response) => response.data);
}

export type ComplaintRowUpdatePayload = Partial<
  Pick<
    ComplaintRow,
    | 'cause'
    | 'temp'
    | 'permanent'
    | 'fzr'
    | 'replyPlan'
    | 'replyActual'
    | 'replyOntime'
    | 'defectPlan'
    | 'defectActual'
    | 'defectOntime'
    | 'closePlan'
    | 'closeActual'
    | 'closeOntime'
  >
>;

export function updateCurrentComplaintRow(
  sourceRow: number,
  body: ComplaintRowUpdatePayload,
): Promise<ComplaintRow> {
  return api.patch(`/imports/current/rows/${sourceRow}`, body).then((response) => response.data.row);
}

export interface HistoricalYearSummary {
  year: string;
  batchId: string;
  filePath: string;
  fileModifiedAt: number | null;
  finishedAt: number | null;
  rowCount: number;
}

export interface ImportBatchSummary {
  id: string;
  status: 'success' | 'failed';
  templateId: string;
  templateLabel: string;
  groupKey: string;
  filePath: string;
  fileName: string;
  fileModifiedAt: number | null;
  finishedAt: number | null;
  rowCount: number;
  errorMessage: string | null;
  isCurrent: boolean;
}

export type SyncFileStatus = 'imported' | 'skipped' | 'failed';

export interface SyncFileResult {
  fileName: string;
  groupKey: string;
  status: SyncFileStatus;
  errorMessage: string | null;
  rowCount: number;
}

export interface RunImportResult {
  ok: boolean;
  skipped: boolean;
  message: string;
  files: SyncFileResult[];
}

export function fetchBatches(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{ items: ImportBatchSummary[]; total: number }> {
  return api.get('/imports/batches', { params }).then((response) => response.data);
}

export function fetchHistoricalYears(): Promise<{ years: HistoricalYearSummary[] }> {
  return api.get('/imports/history/years').then((response) => response.data);
}

export function runImport(): Promise<RunImportResult> {
  return api.post('/imports/run').then((response) => response.data);
}

export type ImportSourceMode = 'import_file' | 'share_root';

export interface WorkbookTemplateMeta {
  id: string;
  label: string;
  fileNamePattern: string;
}

export interface ImportSourceConfig {
  mode: ImportSourceMode;
  importFile: string | null;
  shareRoot: string | null;
  shareUncConfigured: boolean;
  shareUserConfigured: boolean;
  sharePasswordConfigured: boolean;
  templates: WorkbookTemplateMeta[];
}

export interface ScannedWorkbook {
  templateId: string;
  templateLabel: string;
  fileName: string;
  filePath: string;
  modifiedAt: number | null;
  groupKey: string;
  rank: number;
  selected: boolean;
  skipReason: string | null;
}

export interface ImportSourceScanResult {
  ok: boolean;
  message: string;
  files: ScannedWorkbook[];
}

export function fetchImportSource(): Promise<ImportSourceConfig> {
  return api.get('/imports/source').then((response) => response.data);
}

export function scanImportSource(): Promise<ImportSourceScanResult> {
  return api.post('/imports/source/scan').then((response) => response.data);
}
